/**
 * A department reference is stored by the department's stable `_id`, never by
 * its abbreviation — a rename then auto-follows everywhere because every display
 * re-derives the label from the id. Abbreviation is resolved only at the edges:
 * `resolveDepartmentToId` on the Excel/seed boundary in, `departmentIdToWire` on
 * a submit boundary out when the target resolves a department by name/abbr.
 * Bulk endpoints that accept an id (see buildDepartmentDict) can send the id
 * straight through.
 */
export interface DepartmentRefSource {
  _id: string;
  name: string;
  abbreviation?: string;
}

export interface DepartmentSelectOption {
  value: string;
  label: string;
}

/** Dropdown options: id as value (stable), current abbreviation as label. */
export function departmentOptions(departments: DepartmentRefSource[]): DepartmentSelectOption[] {
  return departments.map((department) => ({
    value: department._id,
    label: department.abbreviation
      ? `${department.name} (${department.abbreviation})`
      : department.name
  }));
}

/**
 * Import boundary: an Excel sheet (or a legacy draft) carries an abbreviation or
 * name — resolve it to the department id. An id passed back in resolves to
 * itself (idempotent). A value matching nothing is kept as-is so the cell still
 * shows it and flags it unknown, exactly as an unmatched abbreviation did.
 */
export function resolveDepartmentToId(
  raw: string | undefined,
  departments: DepartmentRefSource[]
): string {
  if (!raw) return '';
  // Abbreviation/name compare case-insensitively — sheets routinely carry 'v'
  // for a stored 'V', and an unresolved value would slip past the duplicate
  // pass only to fail at the backend. Ids stay exact. A name outranks an
  // abbreviation: a department named 'HR' is not 'Human Resources' (HR).
  const needle = raw.trim().toLowerCase();
  const match =
    departments.find((department) => department._id === raw) ??
    departments.find((department) => department.name.toLowerCase() === needle) ??
    departments.find((department) => department.abbreviation?.toLowerCase() === needle);
  return match?._id ?? raw;
}

/**
 * Submit boundary: map the stored id back to the department's CURRENT
 * abbreviation (or name) for a wire that resolves by name/abbr only. An
 * unresolved raw value passes through so the backend surfaces the same
 * "department not found" it always did.
 */
export function departmentIdToWire(value: string, departments: DepartmentRefSource[]): string {
  const match = departments.find((department) => department._id === value);
  return match ? match.abbreviation || match.name : value;
}
